// React subscriptions over the votes Y.Map. Two hooks:
//
//   useVotesUsed(doc, userId) → number of votes this user has cast across all shapes
//   useShapeVotes(doc, shapeId) → { total, byUser } for a single shape
//
// Both observeDeep the votes root so nested per-shape Y.Map updates trigger
// re-renders. For O(100) stickies × O(10) participants this is cheap.

import { useEffect, useMemo, useState } from 'react';
import * as Y from 'yjs';
import {
  getVotesMap,
  totalVotesFor,
  totalVotesUsed,
  votesForShape,
} from './binding/votesMap';

function useVotesVersion(doc: Y.Doc): number {
  const yVotes = useMemo(() => getVotesMap(doc), [doc]);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const handler = () => setVersion((v) => v + 1);
    yVotes.observeDeep(handler);
    return () => yVotes.unobserveDeep(handler);
  }, [yVotes]);
  return version;
}

export function useVotesUsed(doc: Y.Doc, userId: string): number {
  const version = useVotesVersion(doc);
  return useMemo(
    () => totalVotesUsed(doc, userId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc, userId, version],
  );
}

export interface ShapeVoteSnapshot {
  total: number;
  byUser: Map<string, number>;
}

export function useShapeVotes(doc: Y.Doc, shapeId: string): ShapeVoteSnapshot {
  const version = useVotesVersion(doc);
  return useMemo(
    () => ({
      total: totalVotesFor(doc, shapeId),
      byUser: votesForShape(doc, shapeId),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc, shapeId, version],
  );
}
